import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import { translationPropType } from '@eb/i18n';

import LinkedAccountDialog from '../LinkedAccountsDialog';
import FormSubmitButton from '../FormSubmitButton';
import PasswordField from '../PasswordField';
import StaticEmailField from '../StaticEmailField';

import { Button } from '@eb/eds-button';
import { formValidate } from '@eb/eds-utils';
import { TYPE_BUTTON, STYLE_ANCHOR } from '@eb/eds-button';

import { logInValidate as validate } from '../utils';
import { setWindowLocation } from '@eb/http';
import { BASE_URL, LINK_ACCOUNTS_LOG_IN_FORM } from '../../constants/newIndex';
import { GA_FACEBOOK } from '../../constants/analytics';

import CaptchaField from '../CaptchaField';

import { ofuscateEmail } from '../../utils/security';
import MagicLinkAccountSectionModalButton from '../../connectedComponents/MagicLinkAccountSectionModalButton/index';
import '../MagicLinkAccountSection.scss';

const ForgotPasswordLink = ({ handleForgotPassword }) => (
    <div
        className="eds-l-pad-top-2 eds-text--center"
        data-spec="forgot-password-link"
    >
        <Button
            type={TYPE_BUTTON}
            style={STYLE_ANCHOR}
            onClick={handleForgotPassword}
        >
            {gettext('Forgot password')}
        </Button>
    </div>
);

class LinkAccountsLogInForm extends React.PureComponent {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        email: PropTypes.string,
        handleEmailEdit: PropTypes.func,
        buttonCTA: translationPropType,
        handleForgotPassword: PropTypes.func,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        linkedAccountSuccessUrl: PropTypes.string,
        showCaptcha: PropTypes.bool,
        recaptcha2PublicKey: PropTypes.string,
        sourceProvider: PropTypes.string,
        /* Props Provided Via ReduxForm */
        submitFailed: PropTypes.bool,
        submitSucceeded: PropTypes.bool,
        submitting: PropTypes.bool,
        onForceStld: PropTypes.func,
    };

    state = {
        showDialog: false,
    };

    constructor(props) {
        super(props);

        this.dialogTimeout = null;
    }

    componentDidMount() {
        const { onForceStld } = this.props;

        if (onForceStld) {
            onForceStld();
        }
    }

    componentWillReceiveProps({ submitting, submitFailed, error }) {
        const {
            showNotification,
            clearNotification,
            linkedAccountSuccessUrl,
        } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }

        if (linkedAccountSuccessUrl) {
            this.setState({
                showDialog: true,
            });
            this.dialogTimeout = setTimeout(
                () => this._handleCloseDialog(),
                5000,
            );
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;

        clearNotification();
    }

    _handleForgotPassword = () => {
        const {
            handleForgotPassword,
            clearNotification,
            sourceProvider,
        } = this.props;
        const mustLinkAccount = true;

        clearNotification();
        // Send must_link_account param as true, so we can link it after the resetting the password
        handleForgotPassword({
            sourceProvider,
            mustLinkAccount,
        });
    };

    _handleCloseDialog = () => {
        const { linkedAccountSuccessUrl } = this.props;
        const redirectUrl = linkedAccountSuccessUrl
            ? linkedAccountSuccessUrl
            : BASE_URL;

        // If user triggered redirect manually, discard auto-redirect
        if (this.dialogTimeout) {
            clearTimeout(this.dialogTimeout);
        }

        this.setState({
            showDialog: false,
        });

        setWindowLocation(redirectUrl);
    };

    render() {
        const {
            handleSubmit,
            email,
            handleEmailEdit,
            submitting,
            submitSucceeded,
            buttonCTA,
            clearNotification,
            clearAuthNotification,
            submitFailed,
            showCaptcha,
            recaptcha2PublicKey,

            handleSendMailLinkAccount,
        } = this.props;

        const buttonClasses =
            'eds-fx--fade-in-down eds-fx--delay-1 eds-l-mar-top-6';

        const isSubmitDisabled = submitting || submitSucceeded;

        const _handleClearNotification = () => {
            clearNotification && clearNotification();
            clearAuthNotification && clearAuthNotification();
        };

        return (
            <div className="eds-fx--fade-in eds-fx--delay-2">
                <LinkedAccountDialog
                    showDialog={this.state.showDialog}
                    onCloseDialog={this._handleCloseDialog}
                />
                <MagicLinkAccountSectionModalButton
                    modalButtonText={gettext('Send a magic link')}
                    textWithOfuscatedEmail={gettext(
                        `We sent you a link to your email, ${ofuscateEmail({
                            email,
                        })}`,
                    )}
                    onLinkAccount={handleSendMailLinkAccount}
                />
                <div className="split-container__divider eds-l-pad-bot-8 eds-l-pad-top-8">
                    <div className="split-container__divider-text eds-text-bm eds-text-weight--heavy">
                        {gettext('or')}
                    </div>
                </div>
                <p className="eds-text-bm">
                    {gettext('Use email and password')}
                </p>
                <form onSubmit={handleSubmit} noValidate="novalidate">
                    <StaticEmailField
                        email={email}
                        onEmailEdit={handleEmailEdit}
                        clearNotification={_handleClearNotification}
                    />
                    <PasswordField
                        isLogin={true}
                        submitFailed={submitFailed}
                        isStatic={true}
                    />
                    <CaptchaField
                        showCaptcha={showCaptcha}
                        recaptcha2PublicKey={recaptcha2PublicKey}
                        submitFailed={submitFailed}
                    />

                    <FormSubmitButton
                        submitDisabled={isSubmitDisabled}
                        buttonClasses={buttonClasses}
                        buttonCTA={buttonCTA}
                        style="neutral"
                    />
                    <ForgotPasswordLink
                        handleForgotPassword={this._handleForgotPassword}
                    />
                </form>
            </div>
        );
    }
}

export default formValidate({
    validate,
    form: LINK_ACCOUNTS_LOG_IN_FORM,
    touchOnBlur: false,
})(LinkAccountsLogInForm);
